// Keyboard (docs/07 §5): Space pull (hold to repeat), 1–5 hold, QWERT nudge ▲, ASDFG nudge ▼,
// Enter collect/continue, P pay now, Tab odds, Esc close, F1 debug. Gameplay keys are rebindable
// (settings.keys); Tab, Esc, F1 and the bonus-game keys are fixed.
import { audio } from '../audio/audio';
import { controller, getRenderer } from '../render';
import type { KeyAction } from '../sim/types';
import { getGame, getUI, setUI } from '../state/store';

/** While the settings panel listens for a new binding, gameplay keys are ignored. */
let capturing = false;
export function setKeyCapture(on: boolean): void {
  capturing = on;
}

/** Normalises KeyboardEvent.key the way bindings are stored. */
export function keyName(e: KeyboardEvent): string {
  return e.key.toLowerCase();
}

const bound = (action: KeyAction, key: string) => getGame().meta.settings.keys[action] === key;

let spaceHeld = false;
let repeatTimer: ReturnType<typeof setInterval> | null = null;

function inGame(): boolean {
  const ui = getUI();
  return ui.screen === 'machine' && ui.panel === null && !ui.confirmOffer;
}

export function installKeyboard(): void {
  window.addEventListener('pointerdown', () => audio.unlock(), { capture: true });
  window.addEventListener('keydown', (e) => {
    audio.unlock();
    if ((e.target as HTMLElement)?.tagName === 'TEXTAREA' || capturing) return;
    const key = keyName(e);
    const ui = getUI();
    // Trailer capture mode (docs/09 Phase 7): hide the HUD.
    if (e.key === 'F9') {
      e.preventDefault();
      document.documentElement.classList.toggle('trailer');
      return;
    }
    if (e.key === 'F1') {
      e.preventDefault();
      setUI({ debug: !ui.debug });
      return;
    }
    if (e.key === 'Escape') {
      setUI({ panel: null, confirmOffer: false, confirmMolt: false, debug: false });
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      if (ui.screen === 'machine') setUI({ panel: ui.panel === 'odds' ? null : 'odds' });
      return;
    }
    const run = getGame().run;
    // Bonus modes: Hi-Lo ↑/↓ + Enter, Wheel Space/Enter, Teeth Drop ←/→ + Space, Feast Space.
    if (ui.screen === 'machine' && ui.stage === 'bonus' && run?.bonus && !e.repeat) {
      const views = getRenderer()?.bonus;
      if (run.bonus.mode === 'hilo') {
        if (key === 'arrowup' || key === 'w') return void views?.hilo.guess('hi');
        if (key === 'arrowdown' || key === 's') return void views?.hilo.guess('lo');
        if (e.key === 'Enter') return void views?.hilo.collectNow();
      }
      if (run.bonus.mode === 'plinko') {
        if (key === 'arrowleft' || key === 'a') return void views?.plinko.move(-1);
        if (key === 'arrowright' || key === 'd') return void views?.plinko.move(1);
        if (key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          return void views?.plinko.dropNow();
        }
      }
      if (run.bonus.mode === 'feast' && (key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        return void views?.feast.serveNow();
      }
      if (run.bonus.mode === 'wheel' && (key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        return void views?.wheel.stop();
      }
    }
    if (bound('pull', key)) {
      e.preventDefault();
      if (e.repeat || spaceHeld) return;
      spaceHeld = true;
      if (!inGame()) return;
      controller.pull(false);
      repeatTimer = setInterval(() => {
        if (spaceHeld && inGame() && !getUI().busy) controller.pull(false);
      }, getGame().meta.settings.repeatMs);
      return;
    }
    if (!inGame() || !run || e.repeat) {
      if (bound('collect', key) && ui.stage === 'intermission' && ui.screen === 'machine') controller.act({ t: 'CONTINUE' });
      return;
    }
    if (bound('collect', key)) {
      if (ui.stage === 'intermission') controller.act({ t: 'CONTINUE' });
      else if (run.phase === 'nudge') controller.act({ t: 'COLLECT' });
      else if (run.phase === 'gamble') controller.act({ t: 'GAMBLE_DECLINE' });
      return;
    }
    if (bound('gamble', key) && run.phase === 'gamble') return void controller.act({ t: 'GAMBLE_ACCEPT' });
    if (bound('pay', key)) return void controller.act({ t: 'PAY_NOW' });
    if (bound('hand', key)) return void controller.toggleHand();
    for (let i = 1; i <= 5; i++) {
      if (bound(`hold${i}` as KeyAction, key)) return void controller.act({ t: 'TOGGLE_HOLD', reel: i - 1 });
      if (bound(`up${i}` as KeyAction, key)) return void controller.act({ t: 'NUDGE', reel: i - 1, dir: -1 });
      if (bound(`down${i}` as KeyAction, key)) return void controller.act({ t: 'NUDGE', reel: i - 1, dir: 1 });
    }
  });
  window.addEventListener('keyup', (e) => {
    if (bound('pull', keyName(e))) {
      spaceHeld = false;
      if (repeatTimer) clearInterval(repeatTimer);
      repeatTimer = null;
    }
  });
}
